import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { apiUrl } from 'store/constant';

// material-ui
import { Avatar, Box, ButtonBase, Typography, Badge, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { secureLocalStorage } from "store/SecureStorage";
// project imports
import homeLogo from 'assets/images/homelogo.png';
import ProfileSection from './ProfileSection';
import { IconMenu2, IconBell } from '@tabler/icons';
import { getToken } from 'store/token';

const Header = ({ handleLeftDrawerToggle }) => {
    const token = getToken();
    const [orgname, setOrgname] = useState('Organization');
    const [count, setCount] = useState(0);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const displayOrgName = isMobile && orgname.length > 7 ? `${orgname.slice(0, 7)}...` : orgname;

    useEffect(() => {
        const fetchData = async () => {
            // Get organization name
            const savedOrg = secureLocalStorage.get('_or');
            if (savedOrg) {
                try {
                    setOrgname(savedOrg.name || 'Organization');
                } catch (error) {
                    console.error('Error parsing org from localStorage', error);
                }
            }

            // Get notification count
            try {
                const savedUser = secureLocalStorage.get('_us');
                if (!savedUser) return;
                const response = await axios.get(`${apiUrl}/notification/count`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                        user: savedUser.empcode
                    }
                });

                const data = response.data;
                setCount(data.count);
            } catch (err) {
                console.error('Fetch error:', err);
            }
        };

        fetchData();
    }, []);

    // Add inside Header component
    const handleBellClick = () => {
        setCount(0);
        navigate('/trucred/notification');
    };

    const handleOrgNameClick = () => {
        if (isMobile) {
            navigate('/trucred/companyprofile');
        }
    };

    return (
        <>
            {/* Logo & menu toggle button */}
            <Box
                sx={{
                    
                    width: 228,
                    display: 'flex',
                    [theme.breakpoints.down('md')]: {
                        width: 'auto'
                    }
                }}
            >
                <Box component="span" sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
                    <img src={homeLogo} alt="Trucred Logo" style={{ height: 40 }} />
                </Box>
                <ButtonBase sx={{ borderRadius: '12px', overflow: 'hidden' }}>
                    <Avatar
                        variant="rounded"
                        sx={{
                            ...theme.typography.commonAvatar,
                            ...theme.typography.mediumAvatar,
                            transition: 'all .2s ease-in-out',
                            background: theme.palette.secondary.light,
                            color: theme.palette.secondary.dark,
                            '&:hover': {
                                background: theme.palette.secondary.dark,
                                color: theme.palette.secondary.light
                            }
                        }}
                        onClick={handleLeftDrawerToggle}
                        color="inherit"
                    >
                        <IconMenu2 stroke={1.5} size="1.3rem" />
                    </Avatar>
                </ButtonBase>
            </Box>

            {/* Centered organization name */}
            <Box onClick={handleOrgNameClick} sx={{ flexGrow: 1, textAlign: 'center' }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: '30px',
                        color: 'white',
                        cursor: isMobile ? 'pointer' : 'default'
                    }}
                >
                    {displayOrgName}
                </Typography>
            </Box>

            {/* Notification Bell with Badge */}
            <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                <Badge badgeContent={count} color="error" max={99}>
                    <Avatar
                        onClick={handleBellClick}
                        sx={{
                            ...theme.typography.mediumAvatar,
                            bgcolor: theme.palette.primary.light,
                            color: theme.palette.primary.dark,
                            cursor: 'pointer',
                            transition: 'all .2s ease-in-out',
                            '&:hover': {
                                bgcolor: theme.palette.primary.dark,
                                color: theme.palette.primary.light
                            }
                        }}
                    >
                        <IconBell stroke={1.25} size="1.3rem" />
                    </Avatar>
                </Badge>
            </Box>

            {/* Right-side profile dropdown */}
            <ProfileSection />
        </>
    );
};

Header.propTypes = {
    handleLeftDrawerToggle: PropTypes.func
};

export default Header;
